import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-aria-components";

import { TabbedContentBlock } from "@reactivated";

import { MediaChooserBlock } from "../MediaChooserBlock";
import { VideoPlayerControls } from "../VideoPlayerControls";

import styles from "./TabbedContent.module.scss";

export const TabbedContent = ({ value }: { value: TabbedContentBlock }) => {
    const [selectedKey, setSelectedKey] = useState<string>("tab-0");
    const [isOverflowingLeft, setIsOverflowingLeft] = useState(false);
    const [isOverflowingRight, setIsOverflowingRight] = useState(false);
    const [activeVideo, setActiveVideo] = useState<HTMLVideoElement | null>(
        null,
    );
    const panelRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
    const tabListRef = useRef<HTMLDivElement>(null);

    const checkOverflow = useCallback(() => {
        const element = tabListRef.current;
        if (!element) return;
        const { scrollLeft, scrollWidth, clientWidth } = element;
        setIsOverflowingLeft(scrollLeft > 0);
        setIsOverflowingRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    useEffect(() => {
        const element = tabListRef.current;
        if (!element) return;
        checkOverflow();
        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.disconnect();
        };
    }, [checkOverflow]);

    const activeIndex = useMemo(
        () => Number(String(selectedKey).replace("tab-", "")),
        [selectedKey],
    );

    const setPanelRef = (index: number) => (el: HTMLDivElement | null) => {
        panelRefs.current.set(index, el);
    };

    // Scroll active tab to center on mobile
    useEffect(() => {
        const scrollContainer = tabListRef.current;
        if (!scrollContainer) return;
        const activeTab =
            scrollContainer.querySelector<HTMLElement>("[data-selected]");
        if (!activeTab) return;
        const containerRect = scrollContainer.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const scrollLeft =
            activeTab.offsetLeft - containerRect.width / 2 + tabRect.width / 2;
        scrollContainer.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }, [selectedKey]);

    // Auto-play video in active tab, pause others
    useEffect(() => {
        let nextActiveVideo: HTMLVideoElement | null = null;
        panelRefs.current.forEach((el, index) => {
            if (!el) return;
            const video = el.querySelector("video");
            if (!video) return;
            video.preload = "auto";
            video.controls = false;
            video.removeAttribute("controls");
            if (index === activeIndex) {
                nextActiveVideo = video;
                video.muted = true;
                video.playsInline = true;
                video.loop = true;
                video.currentTime = 0;
                video.play().catch(() => {});
            } else {
                video.pause();
                video.currentTime = 0;
            }
        });
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sync active <video> reference; not a cascading render
        setActiveVideo(nextActiveVideo);
    }, [activeIndex]);

    const items = value.items ?? [];

    return (
        <Tabs
            className={styles.root}
            selectedKey={selectedKey}
            onSelectionChange={(key) => setSelectedKey(String(key))}
        >
            <div
                className={styles.tabListWrapper}
                data-overflow-left={isOverflowingLeft}
                data-overflow-right={isOverflowingRight}
            >
                <TabList
                    ref={tabListRef}
                    className={styles.tabList}
                    onScroll={checkOverflow}
                >
                    {items.map((item, index) => (
                        <Tab
                            key={`tab-${index}`}
                            id={`tab-${index}`}
                            className={styles.tab}
                        >
                            {item.label}
                        </Tab>
                    ))}
                </TabList>
            </div>
            {items.map((item, index) => (
                <TabPanel
                    key={`panel-${index}`}
                    id={`tab-${index}`}
                    className={styles.panel}
                >
                    <div className={styles.textBlock}>
                        <h3 className={styles.headline}>{item.headline}</h3>
                        {item.body && (
                            <p className={styles.body}>{item.body}</p>
                        )}
                    </div>
                    <div ref={setPanelRef(index)} className={styles.mediaBlock}>
                        {item.media && (
                            <MediaChooserBlock
                                value={item.media}
                                attrs={{ className: styles.media }}
                            />
                        )}
                        {index === activeIndex &&
                            item.media?.some((m) => m.type === "video") && (
                                <VideoPlayerControls video={activeVideo} />
                            )}
                    </div>
                </TabPanel>
            ))}
        </Tabs>
    );
};
